import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Linkedin, Facebook, Copy, Check, Loader2, Sun, Wand2, Lightbulb } from "lucide-react";
import { api, clearToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const TOPICS = [
  "L'offre : panneaux + batterie, tarif bloqué 0,27 €/kWh pendant 25 ans",
  "Comparatif Sparki XL vs Engie, Luminus et Eneco (économies)",
  "La batterie domestique avec back-up en cas de coupure",
  "150 € de frais de dossier, sans investissement initial",
  "Installation pour particuliers ET sociétés (zonings industriels)",
  "Autoconsommation : produire et stocker sa propre énergie verte",
];

const LENGTHS = [
  { k: "court", l: "Court" },
  { k: "moyen", l: "Moyen" },
  { k: "long", l: "Long" },
];

export default function Studio() {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState("linkedin");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [length, setLength] = useState("moyen");
  const [tone, setTone] = useState("professionnel et chaleureux");
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState("");
  const [copied, setCopied] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/me").catch((e) => {
      if (e?.response?.status === 401) navigate("/login");
    });
  }, [navigate]);

  const loadIdeas = async () => {
    setIdeasLoading(true);
    try {
      const { data } = await api.post("/marketing/ideas");
      setIdeas(data.ideas || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la génération des idées.");
    } finally {
      setIdeasLoading(false);
    }
  };

  const applyIdea = (idea) => {
    setTopic(`${idea.title} — ${idea.angle}`);
    toast.success("Idée sélectionnée ! Cliquez sur « Générer le post ».");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const generate = async () => {
    setLoading(true);
    setPost("");
    try {
      const { data } = await api.post("/marketing/generate-post", {
        platform,
        topic,
        tone,
        length,
        language: "fr",
      });
      setPost(data.post);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la génération.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(post);
    setCopied(true);
    toast.success("Post copié ! Collez-le sur votre réseau.");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-primary px-6 py-6 text-primary-foreground">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent"><Sparkles className="h-5 w-5" /></span>
            <div>
              <h1 className="font-display text-xl font-bold">Studio Marketing IA</h1>
              <p className="text-sm text-primary-foreground/70">Générez des posts LinkedIn & Facebook calibrés</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="secondary" size="sm" className="rounded-full">
              <Link to="/agenda" data-testid="studio-agenda-link"><ArrowLeft className="mr-1.5 h-4 w-4" /> Agenda</Link>
            </Button>
            <Button asChild variant="secondary" size="sm" className="rounded-full">
              <Link to="/" data-testid="studio-home-link">Accueil</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="grid gap-8 lg:grid-cols-2">
          {/* CONFIG */}
          <div className="rounded-3xl border border-border bg-card p-6">
            <h2 className="mb-5 font-display text-lg font-bold">Configurer le post</h2>

            <Label className="mb-2 block text-sm">Plateforme</Label>
            <div className="mb-5 grid grid-cols-2 gap-3">
              <button
                data-testid="platform-linkedin"
                onClick={() => setPlatform("linkedin")}
                className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${platform === "linkedin" ? "border-accent bg-accent text-accent-foreground" : "border-border bg-background hover:border-accent"}`}
              >
                <Linkedin className="h-4 w-4" /> LinkedIn
              </button>
              <button
                data-testid="platform-facebook"
                onClick={() => setPlatform("facebook")}
                className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${platform === "facebook" ? "border-accent bg-accent text-accent-foreground" : "border-border bg-background hover:border-accent"}`}
              >
                <Facebook className="h-4 w-4" /> Facebook
              </button>
            </div>

            <Label className="mb-2 block text-sm">Sujet / angle</Label>
            <select
              data-testid="topic-select"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="mb-3 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
            >
              {TOPICS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <Textarea
              data-testid="topic-custom"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Ou écrivez votre propre sujet..."
              rows={2}
              className="mb-5"
            />

            <Label className="mb-2 block text-sm">Longueur</Label>
            <div className="mb-5 grid grid-cols-3 gap-2">
              {LENGTHS.map((x) => (
                <button
                  key={x.k}
                  data-testid={`length-${x.k}`}
                  onClick={() => setLength(x.k)}
                  className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${length === x.k ? "border-accent bg-accent text-accent-foreground" : "border-border bg-background hover:border-accent"}`}
                >
                  {x.l}
                </button>
              ))}
            </div>

            <Label className="mb-2 block text-sm">Ton</Label>
            <Input data-testid="tone-input" value={tone} onChange={(e) => setTone(e.target.value)} className="mb-6 h-11" />

            <Button
              data-testid="generate-post-button"
              onClick={generate}
              disabled={loading}
              className="h-12 w-full rounded-full bg-accent text-base hover:bg-accent/90"
            >
              {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Génération...</> : <><Wand2 className="mr-2 h-5 w-5" /> Générer le post</>}
            </Button>
          </div>

          {/* RESULT */}
          <div className="rounded-3xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-bold">Votre post</h2>
              {post && (
                <Button data-testid="copy-post-button" onClick={copy} variant="outline" size="sm" className="rounded-full">
                  {copied ? <><Check className="mr-1.5 h-4 w-4" /> Copié</> : <><Copy className="mr-1.5 h-4 w-4" /> Copier</>}
                </Button>
              )}
            </div>
            {post ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="generated-post"
                className="whitespace-pre-wrap rounded-2xl bg-muted/50 p-5 text-sm leading-relaxed"
              >
                {post}
              </motion.div>
            ) : (
              <div className="flex h-full min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-border text-center text-muted-foreground">
                <Sparkles className="h-10 w-10 opacity-30" />
                <p className="mt-4 max-w-xs text-sm">Configurez votre post à gauche puis cliquez sur « Générer ». Le texte prêt à publier apparaîtra ici.</p>
              </div>
            )}
          </div>
        </div>

        <section className="mt-8 rounded-3xl border border-border bg-card p-6" data-testid="ideas-section">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-accent" />
              <h2 className="font-display text-lg font-bold">Idées de posts (Wallonie & Flandre)</h2>
            </div>
            <Button
              data-testid="load-ideas-button"
              onClick={loadIdeas}
              disabled={ideasLoading}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              {ideasLoading ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Génération...</> : <><Sparkles className="mr-1.5 h-4 w-4" /> Proposer des idées</>}
            </Button>
          </div>
          {ideas.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {ideas.map((idea, i) => (
                <button
                  key={i}
                  data-testid={`idea-${i}`}
                  onClick={() => applyIdea(idea)}
                  className="group rounded-2xl border border-border bg-background p-4 text-left transition-colors hover:border-accent"
                >
                  <p className="font-semibold text-foreground group-hover:text-accent">{idea.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{idea.angle}</p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Cliquez sur « Proposer des idées » pour obtenir 8 sujets d'actualité (primes régionales, saisons, témoignages…), adaptés à la Wallonie et à la Flandre. Cliquez ensuite sur une idée pour la charger dans le générateur.
            </p>
          )}
        </section>

        <p className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
          <Sun className="h-3.5 w-3.5 text-accent" /> Généré par IA à partir des informations Solaria & Sparki XL. Relisez avant publication.
        </p>
      </main>
    </div>
  );
}
